import { DateRangeFilter, SearchFilter, SelectFilterItem, } from "@/components/filters";
import { RouteStatus, } from "@/types/route/route.common";
import { makeAutoObservable } from "mobx";
import { DataTableStore } from "./DataTableStore";
import { FiltersStore } from "./FiltersStore";
/**
 * RouteListStore - manages route lists, filtering, pagination
 * Following SOLID principles and project patterns
 * - Single Responsibility: Only manages route list-related state
 * - Dependency Inversion: Depends on abstractions (interfaces) not concrete implementations
 * - Open/Closed: Easy to extend with new functionality
 * - Includes integrated filters functionality following the established pattern
 */
export class RouteListStore {
    constructor(routeService) {
        this.routeService = routeService;
        // Loading and error state
        this.isLoading = false;
        this.error = null;
        this.dataTableStore = new DataTableStore({
            dataLoader: {
                loadData: (params) => this.routeService
                    .list(params.filters, params.page, params.pageSize)
                    .then((result) => ({
                    data: result.data,
                    total: result.count,
                    page: params.page,
                    pageSize: params.pageSize,
                })),
            },
        });
        // Initialize filters store
        this.filtersStore = new FiltersStore();
        makeAutoObservable(this);
        // Initialize route-specific filter items in constructor as requested
        this.initializeRouteFilters();
    }
    // === ROUTE LIST OPERATIONS ===
    /**
     * Refresh the current page after route operations
     */
    async refreshRoutes() {
        await this.dataTableStore.loadData();
    }
    // === FILTERS FUNCTIONALITY ===
    /**
     * Initialize route-specific filter items
     */
    initializeRouteFilters() {
        const filterItems = [
            {
                id: "routeStatus",
                Cmp: SelectFilterItem,
                props: {
                    name: "routeStatus",
                    label: "Статус маршрута",
                    placeholder: "Выберите статус",
                    options: [
                        { label: "Запланирован", value: RouteStatus.PLANNED },
                        { label: "Выполняется", value: RouteStatus.IN_PROGRESS },
                        { label: "Завершен", value: RouteStatus.COMPLETED },
                        { label: "Отменен", value: RouteStatus.CANCELLED },
                    ],
                    allowClear: true,
                    onChange: (value) => {
                        this.filtersStore.updateFilter("routeStatus", value);
                    },
                },
                visible: true,
                order: 1,
            },
            {
                id: "search-filter",
                Cmp: SearchFilter,
                props: {
                    name: "search",
                    label: "Поиск",
                    placeholder: "Поиск по технику...",
                    allowClear: true,
                    showSearchIcon: true,
                    onChange: (value) => this.filtersStore.updateFilter("search", value),
                },
                visible: true,
                order: 4,
            },
            {
                id: "routeDate",
                Cmp: DateRangeFilter,
                props: {
                    name: "routeDate",
                    label: "Дата маршрута",
                    placeholder: ["Дата от", "Дата до"],
                    allowClear: true,
                    onChange: (dates) => {
                        if (Object.values(dates).filter(Boolean).length === 0) {
                            this.filtersStore.clearFilter("routeDate");
                        }
                        else {
                            this.filtersStore.updateFilter("routeDate", dates);
                        }
                    },
                },
                visible: true,
                order: 3,
            },
        ];
        this.filtersStore.setFilterItems(filterItems);
    }
    /**
     * Get current route filters
     */
    getRouteFilters() {
        return this.filtersStore.values;
    }
    /**
     * Clear all filters
     */
    clearAllFilters() {
        this.filtersStore.clearAllFilters();
    }
    /**
     * Clear all data (for cleanup)
     */
    clearAllData() {
        this.clearAllFilters();
        this.dataTableStore.reset();
    }
}
